import { useState, useEffect } from 'react';
import { authAPI, getToken, setToken, removeToken } from '../services/api';
import stateManager from '../services/stateManager';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // console.log('[useAuth] ===== INITIALIZING AUTH =====');
    // console.log('[useAuth] Current URL:', window.location.href);
    // console.log('[useAuth] URL search params:', window.location.search);

    // Check cache first for instant load
    const cachedUser = stateManager.getUser();
    if (cachedUser) {
      // console.log('[useAuth] Using cached user:', cachedUser.email);
      setUser(cachedUser);
    } else {
      // console.log('[useAuth] No cached user found');
    }

    // Check for token in URL (from OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');


    if (error) {
      console.error('[useAuth] ❌ ERROR in URL params:', error);
      // TODO: Show error to user
    }

    if (token) {
      // console.log('[useAuth] ✅ Token found in URL, storing...');
      // console.log('[useAuth] Token preview:', token.substring(0, 20) + '...');
      setToken(token);
      // Clean URL
      // console.log('[useAuth] Cleaning URL...');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // console.log('[useAuth] No token in URL');
    }

    // Verify existing token
    const existingToken = getToken();
    // console.log('[useAuth] Existing token check:', existingToken ? 'FOUND' : 'NOT FOUND');
    if (existingToken) {
      // console.log('[useAuth] Token preview:', existingToken.substring(0, 20) + '...');
      verifyToken();
    } else {
      // console.log('[useAuth] No token to verify, setting loading=false');
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    // console.log('[useAuth] ===== VERIFYING TOKEN =====');
    try {
      // console.log('[useAuth] Calling /api/auth/me...');
      const data = await authAPI.getCurrentUser();
      // console.log('[useAuth] ✅ Token verified successfully');

      stateManager.setUser(data.user);
      setUser(data.user);
    } catch (error) {
      console.error('[useAuth] ❌ Token verification failed:', error.message);
      console.error('[useAuth] Full error:', error);
      // console.log('[useAuth] Removing invalid token...');
      removeToken();
      stateManager.setUser(null);
      setUser(null);
    } finally {
      // console.log('[useAuth] Setting loading=false');
      setIsLoading(false);
    }
  };

  const updateUser = async (updates) => {
    // console.log('[useAuth] Updating user profile:', updates);
    try {
      const data = await authAPI.updateProfile(updates);
      // console.log('[useAuth] Profile updated:', data.user);
      // Update local state with the response (no need to refetch)
      stateManager.setUser(data.user);
      setUser(data.user);

      // Return both user and harmfulContentDetected flag
      return {
        user: data.user,
        harmfulContentDetected: data.harmfulContentDetected || false
      };
    } catch (error) {
      console.error('[useAuth] Profile update failed:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    // console.log('[useAuth] ===== LOGIN INITIATED =====');
    // console.log('[useAuth] Email:', email);
    // console.log('[useAuth] NODE_ENV:', process.env.NODE_ENV);
    // console.log('[useAuth] API_URL:', process.env.REACT_APP_API_URL);
    setIsLoading(true);
    try {
      // console.log('[useAuth] Calling authAPI.login...');
      const data = await authAPI.login(email, password);


      // If we got a token back (dev mode), set user
      if (data.token && data.user) {
        // console.log('[useAuth] ✅ Dev mode - direct token received');
        // console.log('[useAuth] User:', data.user.email);
        stateManager.setUser(data.user);
        setUser(data.user);
        setIsLoading(false);
      } else if (data.ssoUrl) {
        // console.log('[useAuth] 🔄 Production mode - SSO redirect');
        // console.log('[useAuth] SSO URL:', data.ssoUrl);
        // console.log('[useAuth] Will redirect in authAPI.login...');
        // Redirect happens in authAPI.login
      } else {
        console.warn('[useAuth] ⚠️ Unexpected response format:', data);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[useAuth] ❌ Login error:', error.message);
      console.error('[useAuth] Full error:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    // console.log('[useAuth] Logging out...');
    try {
      // authAPI.logout will handle redirecting to Thinkific logout
      // and then back to the app, so no need to clear state here
      stateManager.clearAll();
      await authAPI.logout();
    } catch (error) {
      console.error('[useAuth] Logout failed:', error);
      // Fallback: clear state and reload
      removeToken();
      stateManager.clearAll();
      setUser(null);
      window.location.href = '/';
    }
  };

  return {
    user,
    login,
    logout,
    updateUser,
    isLoading
  };
}
